import currencyFormatter, { FormatOptions } from 'currency-formatter'
import config from 'constants/config'

const dollarPrefixes = {
  AUD: 'A',
  CAD: 'CA',
  HKD: 'HK',
  MOP: 'MOP',
  NZD: 'NZ',
  SGD: 'S',
  TWD: 'NT',
  USD: 'US',
}

const IGNORE_CURRENCY_SYMBOL_LIST = new Set(['SAR', 'AED', 'QAR'])
const SYMBOL_FROM_LEFT_REGIONS = new Set(['NL'])

function getCurrencySymbol(currencyCode: string, defaultSymbol?: string) {
  return IGNORE_CURRENCY_SYMBOL_LIST.has(currencyCode) ?
    currencyCode :
    defaultSymbol
}

export function formatDynamicAmount(amount: number, currencyCode: string, showSign: boolean = false, regionCode?: string): string {
  const options: FormatOptions = {
    code: currencyCode,
    precision: (amount % 1 ? 2 : 0),
    symbol: getCurrencySymbol(currencyCode),
  }

  if (showSign || regionCode && SYMBOL_FROM_LEFT_REGIONS.has(regionCode) && currencyCode === 'EUR') {
    options.format = { pos: `${showSign ? '+' : ''}%s%v`, neg: '-%s%v', zero: '%s%v' }
  }

  return currencyFormatter.format(amount, options)
}

export function formatPreciseAmount(amount: number, currencyCode: string): string {
  return currencyFormatter.format(
    amount,
    { code: currencyCode, precision: 2, symbol: getCurrencySymbol(currencyCode) },
  )
}

export function formatRoundedAmount(amount: number, currencyCode: string): string {
  return currencyFormatter.format(
    amount,
    { code: currencyCode, precision: 0, symbol: getCurrencySymbol(currencyCode) },
  )
}

export function addDollarType(formattedAmount: string, currencyCode: string): string {
  if (!config.multiCurrency) {
    // only brands that have multiple currencies need to add the dollar type, it's unnecessary otherwise
    return formattedAmount
  }

  if (formattedAmount.match(/^\$/)) {
    return ''.concat(dollarPrefixes[currencyCode] || '').concat(formattedAmount)
  }

  if (formattedAmount.match(/^\-\$/)) {
    return '-'.concat(dollarPrefixes[currencyCode] || '').concat(formattedAmount.slice(1))
  }

  return formattedAmount
}

export function formatDynamicAmountWithDollarType(amount: number, currencyCode: string, regionCode?: string): string {
  return addDollarType(
    formatDynamicAmount(amount, currencyCode, false, regionCode),
    currencyCode,
  )
}

export function formatPreciseAmountWithDollarType(amount: number, currencyCode: string): string {
  return addDollarType(
    formatPreciseAmount(amount, currencyCode),
    currencyCode,
  )
}

export function formatRoundedAmountWithDollarType(amount: number, currencyCode: string): string {
  return addDollarType(
    formatRoundedAmount(amount, currencyCode),
    currencyCode,
  )
}

export function blankAmount(currencyCode: string): string {
  const currency = currencyFormatter.findCurrency(currencyCode)
  const currencySymbol = getCurrencySymbol(currencyCode, currency?.symbol)
  return currency?.symbolOnLeft ?
    `${currencySymbol} —` :
    `— ${currencySymbol}`
}

export function blankAmountWithDollarType(currencyCode: string): string {
  const currency = currencyFormatter.findCurrency(currencyCode)
  const currencySymbol = addDollarType(getCurrencySymbol(currencyCode, currency?.symbol) ?? '', currencyCode)
  return currency?.symbolOnLeft ?
    `${currencySymbol} —` :
    `— ${currencySymbol}`
}
